import { Fragment, useEffect } from 'react'
import { useTradingStore } from '../hooks/useTradingStore'
import { Briefcase, FileText, X, TrendingUp, TrendingDown, LogOut } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'

const API_BASE = import.meta.env.PROD ? '/api' : 'http://localhost:9099/api'

type LotLike = {
  quantity: number
  entry_price: number | null
  unrealized_pnl: number | null
  tp_price: number | null
  sl_price: number | null
}
type PositionLike = { avg_entry_price: number; unrealized_pnl: number; lots?: LotLike[] }

// Lots are shown when there is more than one, or any carries a bracket.
function showsLots(lots: LotLike[]) {
  return lots.length > 1 || lots.some(l => l.tp_price != null || l.sl_price != null)
}

// When lot rows are shown, the parent row aggregates them. The backend's
// avg_px_open is a netted average cost that partial exits never reset (and a
// restart zeroes the offsetting realized P&L), so it won't match the lots.
function positionView(position: PositionLike) {
  const lots = position.lots ?? []
  const complete = showsLots(lots)
    && lots.every(l => l.entry_price != null && l.unrealized_pnl != null)
  if (!complete) {
    return { avgEntry: position.avg_entry_price, upnl: position.unrealized_pnl }
  }
  const qty = lots.reduce((s, l) => s + l.quantity, 0)
  return {
    avgEntry: lots.reduce((s, l) => s + l.quantity * (l.entry_price ?? 0), 0) / qty,
    upnl: lots.reduce((s, l) => s + (l.unrealized_pnl ?? 0), 0),
  }
}

export default function Positions() {
  const { positions, orders, tradeHistory, fetchPositions, fetchOrders, fetchTrades } = useTradingStore()

  useEffect(() => {
    fetchPositions()
    fetchOrders()
    fetchTrades()
    const interval = setInterval(() => {
      fetchPositions()
      fetchOrders()
      fetchTrades()
    }, 5000)
    return () => clearInterval(interval)
  }, [fetchPositions, fetchOrders, fetchTrades])

  const handleClosePosition = async (instrumentId: string) => {
    try {
      await axios.post(`${API_BASE}/positions/close/${instrumentId}`)
      fetchPositions()
    } catch (error) {
      console.error('Failed to close position:', error)
    }
  }

  // Calculate totals
  const totalUnrealizedPnL = positions.reduce((sum, p) => sum + positionView(p).upnl, 0)
  const totalRealizedPnL = positions.reduce((sum, p) => sum + p.realized_pnl, 0)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Positions</h1>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-[#666]">Unrealized P&L</p>
            <p className={clsx(
              'text-lg font-bold font-mono',
              totalUnrealizedPnL >= 0 ? 'text-green-400' : 'text-red-400'
            )}>
              ${totalUnrealizedPnL.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#666]">Realized P&L</p>
            <p className={clsx(
              'text-lg font-bold font-mono',
              totalRealizedPnL >= 0 ? 'text-green-400' : 'text-red-400'
            )}>
              ${totalRealizedPnL.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Open Positions */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Briefcase size={16} className="text-blue-400" />
            <h2 className="card-title">Open Positions</h2>
          </div>
          <span className="text-xs text-[#666]">{positions.length} active</span>
        </div>

        {positions.length === 0 ? (
          <div className="text-center py-12 text-[#666]">
            <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
            <p>No open positions</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Instrument</th>
                <th>Side</th>
                <th>Entry Time</th>
                <th className="text-right">Quantity</th>
                <th className="text-right">Avg Entry</th>
                <th className="text-right">Unrealized P&L</th>
                <th className="text-right">Realized P&L</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((position) => {
                const isLong = position.side === 'LONG'
                const lots = position.lots ?? []
                // Earliest real entry fill when known; ts_opened is only
                // reconciliation time after a restart.
                const lotTimes = lots
                  .map((l) => l.entry_time)
                  .filter((t): t is number => t != null)
                const entryTs = lotTimes.length ? Math.min(...lotTimes) : position.ts_opened
                const entryTime = entryTs
                  ? new Date(entryTs / 1_000_000).toLocaleString()
                  : '—'
                const showLots = showsLots(lots)
                const { avgEntry, upnl } = positionView(position)
                return (
                  <Fragment key={position.instrument_id}>
                  <tr>
                    <td className="font-medium text-white">
                      {position.instrument_id}
                    </td>
                    <td>
                      <span className={clsx(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                        isLong ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                      )}>
                        {isLong ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {position.side}
                      </span>
                    </td>
                    <td className="text-[#666] text-xs">{entryTime}</td>
                    <td className="text-right font-mono">
                      {position.quantity}
                    </td>
                    <td className="text-right font-mono">
                      ${avgEntry.toFixed(2)}
                    </td>
                    <td className={clsx(
                      'text-right font-mono font-medium',
                      upnl >= 0 ? 'text-green-400' : 'text-red-400'
                    )}>
                      ${upnl.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className={clsx(
                      'text-right font-mono font-medium',
                      position.realized_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                    )}>
                      ${position.realized_pnl.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="text-center">
                      <button
                        onClick={() => handleClosePosition(position.instrument_id)}
                        className="btn btn-outline text-xs py-1 px-2"
                      >
                        <X size={14} />
                        Close
                      </button>
                    </td>
                  </tr>
                  {showLots && lots.map((lot) => (
                    <tr key={lot.client_order_id} className="text-xs text-[#999]">
                      <td className="pl-6 font-mono">
                        └ {lot.client_order_id.slice(-8)}
                      </td>
                      <td className="font-mono text-[#666]">
                        TP {lot.tp_price != null ? `$${lot.tp_price.toFixed(2)}` : '—'}
                        {' · '}
                        SL {lot.sl_price != null ? `$${lot.sl_price.toFixed(2)}` : '—'}
                      </td>
                      <td className="text-[#666]">
                        {lot.entry_time
                          ? new Date(lot.entry_time / 1_000_000).toLocaleString()
                          : '—'}
                      </td>
                      <td className="text-right font-mono">{lot.quantity}</td>
                      <td className="text-right font-mono">
                        {lot.entry_price != null
                          ? `${lot.inferred ? '≈' : ''}$${lot.entry_price.toFixed(2)}`
                          : '—'}
                      </td>
                      <td className={clsx(
                        'text-right font-mono',
                        lot.unrealized_pnl == null ? 'text-[#666]'
                          : lot.unrealized_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                      )}>
                        {lot.unrealized_pnl != null
                          ? `$${lot.unrealized_pnl.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                          : '—'}
                      </td>
                      <td className="text-right text-[#666]">—</td>
                      <td></td>
                    </tr>
                  ))}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Open Orders */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-amber-400" />
            <h2 className="card-title">Open Orders</h2>
          </div>
          <span className="text-xs text-[#666]">{orders.length} working</span>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-8 text-[#666]">No open orders</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Instrument</th>
                <th>Type</th>
                <th>Side</th>
                <th className="text-right">Quantity</th>
                <th className="text-right">Price</th>
                <th>Status</th>
                <th>Order ID</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const isBuy = order.side === 'BUY'
                const time = new Date(order.ts_init / 1_000_000).toLocaleString()
                // STOP_MARKET has only trigger_price; LIMIT has only price; STOP_LIMIT has both (show trigger).
                const displayPrice = order.trigger_price ?? order.price

                // Detect exit (closing) orders: bracket SL/TP children have parent_order_id,
                // manual closes use reduce_only and/or a CLOSING_BY_ALGO tag.
                const isExit =
                  !!order.parent_order_id
                  || !!order.is_reduce_only
                  || (order.tags ?? []).some(t =>
                       /^(STOP_LOSS|TAKE_PROFIT|EXIT|CLOSE|CLOSING_BY_ALGO)/i.test(t)
                     )
                // Color tracks the position direction: a SELL exit closes a LONG → green;
                // a BUY exit closes a SHORT → red. Entries stay green=LONG, red=SHORT.
                const isPositionLong = isExit ? !isBuy : isBuy
                const sideLabel = isExit
                  ? (isBuy ? 'EXIT SHORT' : 'EXIT LONG')
                  : (isBuy ? 'LONG' : 'SHORT')
                const SideIcon = isExit ? LogOut : (isPositionLong ? TrendingUp : TrendingDown)

                return (
                  <tr key={order.client_order_id}>
                    <td className="text-[#666] text-xs">{time}</td>
                    <td className="font-medium text-white">{order.instrument_id}</td>
                    <td className="text-sm">{order.order_type}</td>
                    <td>
                      <span className={clsx(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                        isPositionLong ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                      )}>
                        <SideIcon size={12} />
                        {sideLabel}
                      </span>
                    </td>
                    <td className="text-right font-mono">{order.quantity}</td>
                    <td className="text-right font-mono">
                      {displayPrice != null ? `$${displayPrice.toFixed(2)}` : '—'}
                    </td>
                    <td className="text-xs">{order.status}</td>
                    <td className="text-xs text-[#666] font-mono">
                      {order.client_order_id.slice(-8)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Trade History */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Trade History</h2>
          <span className="text-xs text-[#666]">Last 30d · {tradeHistory.length} trades</span>
        </div>

        {tradeHistory.length === 0 ? (
          <div className="text-center py-8 text-[#666]">No trade history</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Instrument</th>
                <th>Side</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Price</th>
                <th className="text-right">Commission</th>
                <th className="text-right">P&L</th>
                <th>Order ID</th>
              </tr>
            </thead>
            <tbody>
              {tradeHistory.slice(0, 50).map((trade, index) => {
                const isLong = trade.side === 'LONG' || trade.side === 'BUY'
                const time = new Date(trade.timestamp).toLocaleString()

                return (
                  <tr key={`${trade.order_id}-${trade.timestamp}-${index}`}>
                    <td className="text-[#666] text-xs">{time}</td>
                    <td className="text-sm font-mono">{trade.instrument_id}</td>
                    <td>
                      <span className={clsx(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                        isLong ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                      )}>
                        {isLong ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {trade.side}
                      </span>
                    </td>
                    <td className="text-right font-mono">{trade.quantity}</td>
                    <td className="text-right font-mono">${trade.price.toFixed(2)}</td>
                    <td className="text-right font-mono text-[#666]">{trade.commission.toFixed(2)}</td>
                    <td className={clsx(
                      'text-right font-mono',
                      trade.pnl == null ? 'text-[#666]'
                        : trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'
                    )}>
                      {trade.pnl == null ? '—' : `${trade.pnl >= 0 ? '+' : ''}$${trade.pnl.toFixed(2)}`}
                    </td>
                    <td className="text-xs text-[#666] font-mono">{trade.order_id?.slice(-8) || '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
